import axios from "axios";
import { BACKEND_URL } from "@/lib/env";

const BASE = `${BACKEND_URL}/api/auth`;

export function formatAuthApiError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === "object" && item?.msg ? item.msg : String(item)))
      .join("; ");
  }
  return err?.message || "Authentication request failed";
}

export function resolveAuthLoginFailure(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.error === "account_locked") {
    return {
      message: detail.message || formatAuthApiError(err),
      accountLocked: true,
    };
  }
  if (status === 403 && !detail) {
    return {
      message:
        "Sign-in was blocked by the API gateway (403). Clear site data for localhost:3001, "
        + "retry without a stale session, or confirm the same password works on "
        + "https://halo2-uat.junolabs.ai. If it persists, share the x-amz-apigw-id from DevTools with infra.",
      accountLocked: false,
    };
  }
  return {
    message: formatAuthApiError(err),
    accountLocked: false,
  };
}

const COOKIE_AUTH = {
  withCredentials: true,
  skipUnauthorizedLogout: true,
  skipAuthRetry: true,
};

export async function authConfig() {
  const { data } = await axios.get(`${BASE}/config`, {
    skipUnauthorizedLogout: true,
    skipAuthRetry: true,
  });
  return data;
}

export async function authComplete() {
  const { data } = await axios.get(`${BASE}/complete`, {
    ...COOKIE_AUTH,
    headers: { Accept: "application/json" },
  });
  return data;
}

export async function authLogout() {
  try {
    await axios.post(`${BASE}/logout`, {}, COOKIE_AUTH);
  } catch {
    // Halo sign-out must still clear the SPA session if authapi is unreachable.
  }
}

export async function authLogin(body) {
  const { data } = await axios.post(
    `${BASE}/login`,
    {
      email: body.email.trim().toLowerCase(),
      password: body.password,
    },
    {
      ...COOKIE_AUTH,
      skipAuthRetry: true,
    },
  );
  return data;
}

export async function authCompleteNewPassword(body) {
  const { data } = await axios.post(`${BASE}/new-password`, {
    email: body.email.trim().toLowerCase(),
    session: body.session,
    new_password: body.newPassword,
    user_attributes: body.userAttributes || {},
  });
  return data;
}

export async function authRefresh(refreshToken) {
  const body = refreshToken ? { refresh_token: refreshToken } : {};
  const { data } = await axios.post(`${BASE}/refresh`, body, COOKIE_AUTH);
  return data;
}

export async function authForgotPassword(email) {
  const { data } = await axios.post(`${BASE}/forgot-password`, {
    email: email.trim().toLowerCase(),
  });
  return data;
}

export async function authConfirmForgotPassword(body) {
  const { data } = await axios.post(`${BASE}/confirm-forgot-password`, {
    email: body.email.trim().toLowerCase(),
    code: body.code.trim(),
    new_password: body.newPassword,
  });
  return data;
}

export function isBackendAuthEnabled() {
  return Boolean(BACKEND_URL?.trim());
}
